import * as React from "react";
/* global PowerPoint, Office */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  extractPresentationContent,
  exportSlideAsImage,
  streamSlideThumbnails,
} from "../services/slideService";
import {
  getDeckPlanTransientContext,
  reattachDeckPlanTransientContext,
  stripDeckPlanTransientContext,
  type DeckPlan,
} from "../services/ai/deckPlannerService";
import { generateId, type TabsState } from "../types";
import { RUN_OFFICE_EXTRACTOR_MINIMUM_SHAPE_FILTER } from "../services/factset/intentBridgeService";
import { type BrandId } from "../services/ai/brandRegistry";
import { runAgenticDeckBuild } from "./agenticDeckBuild";
import {
  createPptxLangfuseRunContext,
  postPptxLangfuseOutcome,
} from "../langfuseTrace";
import {
  trackDeckCreationCompleted,
  trackDeckCreationStarted,
  type TrackDeckCreationCompletedParams,
} from "../services/analytics/product";
import { shouldUseFactSetBridge } from "../services/presentationHost";
import {
  CreationProgress,
  StyleConfig,
  ExistingSlidePreview,
  type AllocationOptions,
} from "../components/DeckCreator/DeckCreator";
import { attachSyntheticMessageToSlideTab } from "../utils";

export { attachSyntheticMessageToSlideTab };

interface DeckCancellationToken {
  cancelled: boolean;
  controller: AbortController;
}

// Langfuse and the analytics catalog spell these three outcomes differently.
// The analytics endpoint rejects an unknown `status` with a 400 that only
// console-warns, so drift here would silently stop the event reaching Amplitude.
const DECK_CREATION_ANALYTICS_STATUS: Record<
  "completed" | "failed" | "cancelled",
  TrackDeckCreationCompletedParams["status"]
> = {
  completed: "complete",
  failed: "error",
  cancelled: "cancelled",
};

interface UseDeckCreationProps {
  aiMode: "chat" | "create" | "review";
  setAiMode: (mode: "chat" | "create" | "review") => void;
  syncTabsWithPresentation: (reason: string) => Promise<void>;
  log: (message: string) => void;
  /**
   * Tab-state setter. Lets a task-grounded agentic build attach per-slide
   * citation messages to the generated slide tabs so `SlideCitationsView`
   * populates, matching the chat-edit rendering path.
   */
  setTabsState: React.Dispatch<React.SetStateAction<TabsState>>;
  accountScopeKey?: string | null;
  companyId?: string | null;
  onDeckOperationSettled?: (
    plan: DeckPlan,
    status: "completed" | "failed" | "cancelled"
  ) => void;
}

export const useDeckCreation = ({
  aiMode,
  setAiMode,
  syncTabsWithPresentation,
  log,
  setTabsState,
  accountScopeKey,
  companyId,
  onDeckOperationSettled,
}: UseDeckCreationProps) => {
  const [deckProgressByOperationId, setDeckProgressByOperationId] = useState<
    Record<string, CreationProgress>
  >({});
  const [activeDeckOperationId, setActiveDeckOperationId] = useState<string | null>(null);
  const [deckCreationPlan, setDeckCreationPlan] = useState<DeckPlan | null>(null);
  const [existingSlidePreviews, setExistingSlidePreviews] = useState<ExistingSlidePreview[]>([]);
  // Two views over the same operation-id ordering:
  //   - `operationOrderRef.current` for SYNCHRONOUS read-after-write
  //     inside state-updater callbacks (e.g. picking the next active
  //     deck after one completes — see the `setActiveDeckOperationId`
  //     updater further down).
  //   - `operationOrder` state for declarative React dependents (the
  //     `ongoingDeckCreationProgress` memo below). Without a state
  //     mirror, mutating only the ref would leave the memo stale with
  //     no observable trigger to recompute.
  // The two are written together via `updateOperationOrder`.
  const operationOrderRef = useRef<string[]>([]);
  const [operationOrder, setOperationOrder] = useState<string[]>([]);
  const updateOperationOrder = useCallback(
    (mutator: (prev: string[]) => string[]) => {
      const next = mutator(operationOrderRef.current);
      if (next === operationOrderRef.current) return;
      operationOrderRef.current = next;
      setOperationOrder(next);
    },
    []
  );
  const activeDeckOperationIdRef = useRef<string | null>(null);
  const deckOwnerScopeRef = useRef(accountScopeKey ?? null);
  const [deckOwnerScope, setDeckOwnerScope] = useState(accountScopeKey ?? null);
  const deckStateIsCurrent =
    deckOwnerScope === (accountScopeKey ?? null);
  const isCreatingDeck =
    deckStateIsCurrent && Object.keys(deckProgressByOperationId).length > 0;
  const deckCreationProgress = deckStateIsCurrent && activeDeckOperationId
    ? deckProgressByOperationId[activeDeckOperationId] ?? null
    : null;
  const ongoingDeckCreationProgress = useMemo(
    () => {
      if (!deckStateIsCurrent) return [];
      return operationOrder
        .map((operationId) => {
          const progress = deckProgressByOperationId[operationId];
          if (!progress) return null;
          return { operationId, progress };
        })
        .filter((entry): entry is { operationId: string; progress: CreationProgress } => !!entry);
    },
    [deckStateIsCurrent, operationOrder, deckProgressByOperationId]
  );
  useEffect(() => {
    activeDeckOperationIdRef.current = activeDeckOperationId;
  }, [activeDeckOperationId]);

  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  const cancellationRef = useRef<Map<string, DeckCancellationToken>>(new Map());
  const mountedRef = useRef(true);
  const accountScopeKeyRef = useRef(accountScopeKey ?? null);
  React.useLayoutEffect(() => {
    accountScopeKeyRef.current = accountScopeKey ?? null;
  });

  const cancelAllDeckOperations = useCallback(() => {
    for (const token of cancellationRef.current.values()) {
      token.cancelled = true;
      token.controller.abort();
    }
    cancellationRef.current.clear();
    operationOrderRef.current = [];
    setOperationOrder([]);
    setDeckProgressByOperationId({});
    setActiveDeckOperationId(null);
    setDeckCreationPlan(null);
    setExistingSlidePreviews([]);
    setAiMode("chat");
  }, [setAiMode]);

  useEffect(() => {
    const nextScope = accountScopeKey ?? null;
    if (deckOwnerScopeRef.current === nextScope) return;
    deckOwnerScopeRef.current = nextScope;
    setDeckOwnerScope(nextScope);
    cancelAllDeckOperations();
  }, [accountScopeKey, cancelAllDeckOperations]);

  useEffect(
    () => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        for (const token of cancellationRef.current.values()) {
          token.cancelled = true;
          token.controller.abort();
        }
        cancellationRef.current.clear();
        operationOrderRef.current = [];
      };
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const previewAccountScopeKey = accountScopeKey ?? null;
    const previewIsCurrent = () =>
      !cancelled &&
      mountedRef.current &&
      accountScopeKeyRef.current === previewAccountScopeKey;
    if (aiMode === "create") {
      (async () => {
        try {
          if (shouldUseFactSetBridge()) {
            // FactSet-only: progressive thumbnail streaming via the intent
            // bridge. We only need each slide's shape *count* to decide
            // which thumbnails are worth streaming, so request the minimum-
            // shape filter (every per-shape property leaf turned off). Per
            // the f2_docs spec, identity scalars are always emitted, so
            // `slide.shapes.length` stays accurate.
            const content = await extractPresentationContent(undefined, {
              forceRefresh: true,
              filters: {
                slides: {
                  notesPage: false,
                  background: false,
                  headersFooters: false,
                  shapes: RUN_OFFICE_EXTRACTOR_MINIMUM_SHAPE_FILTER,
                },
              },
            });
            const shapeCountByIndex = new Map<number, number>();
            for (const slide of content.slides) {
              const shapeCount = slide.shapes?.length || 0;
              if (shapeCount > 0) {
                shapeCountByIndex.set(slide.index, shapeCount);
              }
            }
            const previewsByIndex = new Map<number, ExistingSlidePreview>();
            await streamSlideThumbnails(
              ({ index, thumbnail }) => {
                if (!previewIsCurrent()) return;
                const shapeCount = shapeCountByIndex.get(index);
                if (!shapeCount || !thumbnail || thumbnail.length <= 100) {
                  return;
                }
                previewsByIndex.set(index, { index, thumbnail, shapeCount });
                const ordered = Array.from(previewsByIndex.values()).sort(
                  (left, right) => left.index - right.index
                );
                setExistingSlidePreviews(ordered);
              },
              { height: 220 }
            );
            if (previewIsCurrent()) {
              const ordered = Array.from(previewsByIndex.values()).sort(
                (left, right) => left.index - right.index
              );
              setExistingSlidePreviews(ordered);
              logRef.current(
                `Captured ${ordered.length} existing slide thumbnails for base-slide selection`
              );
            }
            return;
          }

          // OfficeJS path: byte-identical to the pre-change baseline.
          const content = await extractPresentationContent();
          const previews: ExistingSlidePreview[] = [];
          for (let i = 0; i < content.slides.length; i++) {
            if (!previewIsCurrent()) return;
            const slide = content.slides[i];
            const shapeCount = slide.shapes?.length || 0;
            if (shapeCount === 0) continue;
            try {
              const thumb = await exportSlideAsImage(i);
              if (thumb && thumb.length > 100) {
                previews.push({ index: i, thumbnail: thumb, shapeCount });
              }
            } catch {
              // ignore individual failures
            }
          }
          if (previewIsCurrent()) {
            setExistingSlidePreviews(previews);
            logRef.current(
              `Captured ${previews.length} existing slide thumbnails for base-slide selection`
            );
          }
        } catch {
          // no existing slides or error — leave empty
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [accountScopeKey, aiMode]);

  if (aiMode !== "create" && existingSlidePreviews.length > 0) {
    setExistingSlidePreviews([]);
  }

  const handleCancelCreation = useCallback(
    (operationId?: string) => {
      const targetOperationId = operationId ?? activeDeckOperationIdRef.current ?? activeDeckOperationId;
      if (targetOperationId) {
        const cancellationToken = cancellationRef.current.get(targetOperationId);
        if (cancellationToken && !cancellationToken.cancelled) {
          cancellationToken.cancelled = true;
          cancellationToken.controller.abort();
          logRef.current("Deck creation cancelled by user");
        }
        updateOperationOrder((prev) => prev.filter((id) => id !== targetOperationId));
        setDeckProgressByOperationId((prev) => {
          if (!prev[targetOperationId]) return prev;
          const next = { ...prev };
          delete next[targetOperationId];
          return next;
        });
        setActiveDeckOperationId((current) => {
          if (current !== targetOperationId) return current;
          const pending = operationOrderRef.current.filter((id) => id !== targetOperationId);
          return pending[pending.length - 1] ?? null;
        });
      }
      // Always navigate back to chat on the user-initiated cancel
      // gesture (no `operationId` arg), even when there's no active
      // operation yet. This is the only exit affordance from the
      // DeckCreator config / review steps that run before
      // `createDeck` has fired — early-returning here would strand
      // the user in `aiMode === "create"` with no way to back out.
      // For explicit per-operation cancellation of a non-active
      // background deck, leave the UI alone so other in-flight decks
      // don't yank focus away from whatever the user is looking at.
      if (!operationId || targetOperationId === activeDeckOperationId) {
        setAiMode("chat");
      }
    },
    [activeDeckOperationId, setAiMode, updateOperationOrder]
  );

  const createDeck = useCallback(
    async (
      plan: DeckPlan,
      onProgress: (p: CreationProgress) => void,
      styleConfig?: StyleConfig,
      allocationOptions?: AllocationOptions,
      brandId?: BrandId | null
    ) => {
      const operationAccountScopeKey = accountScopeKey ?? null;
      const operationIsCurrent = () =>
        mountedRef.current &&
        accountScopeKeyRef.current === operationAccountScopeKey;
      if (!operationIsCurrent()) return;
      const operationPlan = reattachDeckPlanTransientContext(
        JSON.parse(
          JSON.stringify(stripDeckPlanTransientContext(plan))
        ) as DeckPlan,
        getDeckPlanTransientContext(plan)
      );
      // Deck creation runs the approved plan through the unified chat agent
      // (apply_edits / get_slides_info). We register an operation so the App-
      // level deck state (isCreatingDeck, ongoingDeckCreationProgress) and
      // cancellation are wired, then delegate to runAgenticDeckBuild.
      const operationId = generateId();
      const controller = new AbortController();
      const langfuseStartedAt = Date.now();
      const langfuseRunContext = createPptxLangfuseRunContext({
        mode: "create",
        hasContext: Boolean(allocationOptions?.targetPresentationRef),
        plannerRunId: operationPlan.plannerRunId,
      });
      cancellationRef.current.set(operationId, { cancelled: false, controller });
      updateOperationOrder((prev) => [...prev, operationId]);
      activeDeckOperationIdRef.current = operationId;
      setActiveDeckOperationId(operationId);
      setDeckProgressByOperationId((prev) => ({
        ...prev,
        [operationId]: {
          currentSlide: 0,
          totalSlides: operationPlan.slides.length,
          currentSlideTitle: "Initializing...",
          status: "creating",
          message: "Preparing deck generation...",
          completedSlides: [],
        },
      }));

      const brandStyle = brandId ?? null;
      const taskOutputUsed = operationPlan.groundingMode === "run_output";
      trackDeckCreationStarted({
        requestedCount: operationPlan.slides.length,
        brandStyle,
        hasReferenceDeck: styleConfig?.source === "existing",
        taskOutputUsed,
      });

      // Forward every progress tick into BOTH the caller's onProgress (the
      // DeckCreator's localProgress) and deckProgressByOperationId. DeckCreator
      // renders `externalProgress ?? localProgress`, so the registered
      // operation's progress is what the UI shows during the build.
      //
      // Ticks are also the only source for the slide counts on the completion
      // event: `runAgenticDeckBuild` resolves void, and the failure and
      // cancellation paths unwind without returning anything at all.
      //
      // The last tick is also how a failure is detected at all:
      // `runAgenticDeckBuild` reports a stopped build by emitting a tick with
      // `status: "error"` and then resolving, so the `catch` below never runs and
      // only the tick distinguishes a failed build from a finished one.
      let finalSlideCount = operationPlan.slides.length;
      let builtSlideCount = 0;
      let lastProgress: CreationProgress | null = null;
      const handleProgress = (progress: CreationProgress): void => {
        if (!operationIsCurrent() || controller.signal.aborted) return;
        lastProgress = progress;
        finalSlideCount = progress.totalSlides;
        builtSlideCount = progress.completedSlides.length;
        onProgress(progress);
        setDeckProgressByOperationId((prev) =>
          prev[operationId] ? { ...prev, [operationId]: progress } : prev
        );
      };

      let langfuseStatus: "completed" | "failed" | "cancelled" = "completed";
      let langfuseErrorType: string | undefined;
      try {
        await runAgenticDeckBuild({
          plan: operationPlan,
          onProgress: handleProgress,
          brandId: brandId ?? null,
          presentationRef: allocationOptions?.targetPresentationRef ?? null,
          insertAfterCurrent: allocationOptions?.insertAfterCurrent ?? true,
          styleConfig: styleConfig ?? null,
          signal: controller.signal,
          log: (message: string) => {
            if (operationIsCurrent()) logRef.current(message);
          },
          syncTabsWithPresentation: (reason) =>
            operationIsCurrent()
              ? syncTabsWithPresentation(reason)
              : Promise.resolve(),
          setAiMode: (mode) => {
            if (operationIsCurrent()) setAiMode(mode);
          },
          setTabsState: (updater) => {
            if (!operationIsCurrent()) return;
            setTabsState((current) => {
              if (!operationIsCurrent()) return current;
              return typeof updater === "function"
                ? updater(current)
                : updater;
            });
          },
          langfuseContext: langfuseRunContext,
          companyId,
        });
      } catch (error) {
        langfuseStatus = controller.signal.aborted ? "cancelled" : "failed";
        langfuseErrorType = error instanceof Error ? error.message : String(error);
        if (operationIsCurrent()) {
          throw error;
        }
      } finally {
        if (controller.signal.aborted) langfuseStatus = "cancelled";
        cancellationRef.current.delete(operationId);
        if (operationIsCurrent()) {
          const durationMs = Date.now() - langfuseStartedAt;
          postPptxLangfuseOutcome(langfuseRunContext, {
            status: langfuseStatus,
            durationMs,
            slidesRequested: operationPlan.slides.length,
            ...(langfuseErrorType ? { errorType: langfuseErrorType } : {}),
          });
          // A build that stopped without throwing is still a failure. This is
          // deliberately applied to the analytics status only: Langfuse is the
          // source of truth for run outcomes and records this build as completed,
          // so correcting it there is a separate change with its own baseline.
          const analyticsStatus =
            langfuseStatus === "completed" && lastProgress?.status === "error"
              ? "failed"
              : langfuseStatus;
          trackDeckCreationCompleted({
            requestedCount: operationPlan.slides.length,
            finalCount: finalSlideCount,
            successCount: builtSlideCount,
            durationMs,
            status: DECK_CREATION_ANALYTICS_STATUS[analyticsStatus],
            brandStyle,
            taskOutputUsed,
          });
          try {
            onDeckOperationSettled?.(operationPlan, analyticsStatus);
          } catch (error) {
            logRef.current(
              `Deck operation cleanup failed: ${
                error instanceof Error ? error.message : String(error)
              }`
            );
          }
          updateOperationOrder((prev) => prev.filter((id) => id !== operationId));
          setDeckProgressByOperationId((prev) => {
            if (!prev[operationId]) return prev;
            const next = { ...prev };
            delete next[operationId];
            return next;
          });
          setActiveDeckOperationId((current) => {
            if (current !== operationId) return current;
            const pending = operationOrderRef.current;
            return pending[pending.length - 1] ?? null;
          });
        }
      }
    },
    [
      syncTabsWithPresentation,
      setAiMode,
      setTabsState,
      updateOperationOrder,
      onDeckOperationSettled,
      accountScopeKey,
      companyId,
    ]
  );

  return {
    deckCreationProgress,
    ongoingDeckCreationProgress,
    deckCreationPlan: deckStateIsCurrent ? deckCreationPlan : null,
    isCreatingDeck,
    existingSlidePreviews: deckStateIsCurrent ? existingSlidePreviews : [],
    handleCancelCreation,
    cancelAllDeckOperations,
    createDeck,
  };
};
